'use client'

import { useMemo } from 'react'

import { mainnet } from 'wagmi/chains'

import { useAutomations } from '~/app/automations/useAutomations'
import { SupportedChainId } from '~/constants/chains'
import { usePayeeLookupByAddress } from '~/hooks/compliance/usePayeeLookup'
import { useActiveOrg } from '~/hooks/org'
import { useContactLookup } from '~/hooks/useContacts'
import { resolveDisplayName } from '~/hooks/useDisplayName/resolveDisplayName'
import { useAddressLookup } from '~/hooks/useENS'
import { useFarcasterLookup } from '~/hooks/useFarcaster'
import { usePrivacyMode } from '~/hooks/usePrivacyMode'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { IAddress, SmartAccountRole } from '~/types'
import { Address } from '~/utils/address'

export function useDisplayName(
  address?: IAddress,
  chainId: SupportedChainId = mainnet.id,
  shouldShortenAddress: boolean = true,
) {
  const { org } = useActiveOrg()
  const { smartAccounts } = useSmartAccounts()
  const { automations } = useAutomations()

  const { data: ens } = useAddressLookup(address, chainId)
  const isPrivacyModeOn = usePrivacyMode()
  const { farcaster } = useFarcasterLookup(address)
  const { data: contact } = useContactLookup(address)
  const { payee } = usePayeeLookupByAddress(address)

  const isTreasury = useMemo(() => {
    return smartAccounts.some(
      (account) =>
        account.address === address &&
        account.role === SmartAccountRole.TREASURY,
    )
  }, [smartAccounts, address])

  const bankAccount = useMemo(() => {
    if (org?.bridge?.externalAccounts)
      return org.bridge?.externalAccounts.find(({ liquidationAddress }) =>
        Address.equal(address, liquidationAddress),
      )
  }, [org, address])

  const automation = useMemo(
    () =>
      automations.find(
        ({ accountAddress }) =>
          accountAddress && Address.equal(address, accountAddress),
      ),
    [automations, address],
  )

  const displayName = resolveDisplayName({
    address,
    bankAccountName: bankAccount?.name,
    automationName: automation?.name,
    smartAccountName: contact?.smartAccountName,
    orgLabel: contact?.orgLabel,
    ens,
    farcasterDisplayName: farcaster?.displayName,
    farcasterUsername: farcaster?.username,
    payeeOrgName: payee?.payeeOrg?.name,
    payeeEmail: payee?.payeeInvitedEmail,
    isTreasury,
    isPrivacyModeOn,
    shouldShortenAddress,
  })

  return useMemo(
    () => ({
      displayName,
      smartAccountName: contact?.smartAccountName,
      orgLabel: contact?.orgLabel,
      ens,
      farcaster,
      payee,
      bankAccount,
      automation,
      isTreasury,
    }),
    [
      displayName,
      contact?.smartAccountName,
      contact?.orgLabel,
      ens,
      farcaster,
      payee,
      bankAccount,
      automation,
      isTreasury,
    ],
  )
}
